import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { useImageCrop } from '@/hooks/useImageCrop';
import { useUploadFile } from '@/hooks/useUploadFile';
import { formatBytes } from '@/lib/utils/format-bytes';
import type { UploadKind, UploadResult } from '@/api/uploads.api';
import { COMMON_STRINGS } from '@/lib/constants/common-strings';
import { getUploadErrorMessage } from '@/lib/utils/upload-error.utils';

interface FileUploadFieldProps {
  /** Dropzone title (e.g. "Upload Company Logo"). */
  label: string;
  /** Grey hint under the title (e.g. "PNG or JPG, square recommended, max 2MB"). */
  hint?: string;
  kind: UploadKind;
  accept: string;
  /** True when a file is already attached; shows the done state. */
  uploaded?: boolean;
  variant?: 'rect' | 'circle';
  onUploaded: (result: UploadResult) => void;
  onClear: () => void;
  /** External validation error (e.g. "required" - RHF), shown when no upload-specific error is active. */
  error?: string;
  /**
   * Fired whenever an upload starts or finishes, so the SURROUNDING FORM can
   * disable Save while one is in flight.
   *
   * The upload runs inside this component, so without this the form cannot tell
   * one is happening - and saving mid-transfer silently drops the file: the key
   * has not been set yet, the payload omits it, and the save succeeds against
   * the OLD file with no error anywhere.
   */
  onUploadingChange?: (isUploading: boolean) => void;
}

/**
 * Upload control: dashed dropzone that runs the presign->PUT flow via
 * useUploadFile and reports the S3 key to the parent form. Bytes go straight
 * to S3, never through the app (FR-20/NFR-10). Promoted from
 * `features/startups/` to `common/` once a second feature (admin pages,
 * MYN-196) needed the same composite (mnr-standards promotion rule).
 */
export function FileUploadField({
  label,
  hint,
  kind,
  accept,
  uploaded = false,
  variant = 'rect',
  onUploaded,
  onClear,
  error,
  onUploadingChange,
}: FileUploadFieldProps) {
  const upload = useUploadFile();

  useEffect(() => {
    onUploadingChange?.(upload.isPending);
  }, [upload.isPending, onUploadingChange]);
  // Only an upload made in THIS session carries a name and size - the browser
  // never hands them back for a file the member saved on a previous visit, and
  // the backend stores no original filename. So a reloaded form shows the
  // generic "uploaded" label, and only a fresh pick shows real details.
  const [picked, setPicked] = useState<{ name: string; size: string } | null>(null);

  // A PNG/JPEG is cropped to the kind's locked frame first (FR-121); a kind without
  // one (pitch deck, admin images) uploads exactly as before.
  const crop = useImageCrop(kind, (file) =>
    upload.mutate(
      { kind, file },
      {
        onSuccess: (result) => {
          setPicked({ name: result.fileName, size: formatBytes(result.sizeBytes) });
          onUploaded(result);
        },
      },
    ),
  );

  const errorMessage = upload.isError
    ? getUploadErrorMessage(upload.error, {
        tooLarge: COMMON_STRINGS.UPLOAD.TOO_LARGE,
        fallback: COMMON_STRINGS.UPLOAD.ERROR,
      })
    : error;

  // A failed replacement outranks the done state: with a file already attached,
  // "done" used to win and showed a green check beside the failure (MYN-271).
  const state = upload.isPending
    ? 'uploading'
    : upload.isError
      ? 'error'
      : uploaded
        ? 'done'
        : errorMessage
          ? 'error'
          : 'idle';

  return (
    <div className={`flex flex-col gap-1 ${variant === 'rect' ? 'h-full' : ''}`.trim()}>
      <Dropzone
        label={label}
        title={label}
        hint={hint}
        variant={variant}
        state={state}
        fileName={uploaded ? (picked?.name ?? COMMON_STRINGS.UPLOAD.UPLOADED) : undefined}
        fileSize={uploaded ? picked?.size : undefined}
        progress={upload.progress}
        accept={accept}
        error={errorMessage}
        onSelect={crop.requestFile}
      />
      {crop.cropDialog}
      {uploaded && (
        <Button
          type="button"
          variant="link"
          className="self-start text-sm"
          onClick={() => {
            setPicked(null);
            onClear();
          }}
        >
          {COMMON_STRINGS.UPLOAD.REMOVE}
        </Button>
      )}
    </div>
  );
}
